import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Snackbar,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { collectionPointsService, CollectionPoint, SnapshotStructure } from "../../../services/collectionpoints-service";
import { dataPrincipalsService, DataPrincipalListItem, DataPrincipalDetails } from "../../../services/dataPrincipals-service";
import { noticesService, PurposeConsentStatus, AssignmentCompletionStatus } from "../../../services/notices-service";
import { vaultService } from "../../../services/vault-service";
import EffectiveDateSection, {
  dateInputToIsoStartOfDay,
  todayDateInputValue,
} from "./EffectiveDateSection";
import { getApiErrorMessage } from "../../../utils/api-error";

type IdentifierType = "EMAIL" | "PHONE";
type DeliveryMethod = "SMS" | "WHATSAPP" | "BOTH" | "EMAIL";
type SearchType = "DPID" | "TOKEN" | "EXTERNAL_ID" | "EMAIL" | "PHONE";

// Country codes for phone numbers (India first as default)
const COUNTRY_CODES: { code: string; dialCode: string; name: string }[] = [
  { code: 'IN', dialCode: '+91', name: 'India' },
  { code: 'US', dialCode: '+1', name: 'United States' },
  { code: 'GB', dialCode: '+44', name: 'United Kingdom' },
  { code: 'AU', dialCode: '+61', name: 'Australia' },
  { code: 'CA', dialCode: '+1', name: 'Canada' },
  { code: 'DE', dialCode: '+49', name: 'Germany' },
  { code: 'FR', dialCode: '+33', name: 'France' },
  { code: 'JP', dialCode: '+81', name: 'Japan' },
  { code: 'CN', dialCode: '+86', name: 'China' },
  { code: 'SG', dialCode: '+65', name: 'Singapore' },
];

type DummyDpResult = {
  dpId: number;
  identifierType: IdentifierType;
  identifierDisplay: string;
  formatPreservingToken?: string | null; // Minor's masked token (for display)
  vaultId?: number | null; // Minor's vault ID (for click-to-reveal)
  isMinor: boolean;
  hasGuardianContact: boolean;
  // Parent/Guardian information (for minor DPs)
  parentIdentifierType?: string | null;
  parentIdentifierValue?: string | null; // Only for test data
  parentVaultId?: number | null;
  parentFormatPreservingToken?: string | null;
};

// Removed DummyNotice type - using real CollectionPoint interface

export interface SendConsentNoticeModalProps {
  open: boolean;
  onClose: () => void;
}

const STEPS = ["Find or Create DP", "Select Consent Notice", "Review & Send", "Complete (OTP / Refresh)"];

/**
 * Phase 1 dummy modal to lock UX before wiring backend calls.
 * - No API calls
 * - Uses mock DP search results and mock notices
 */
const SendConsentNoticeModal: React.FC<SendConsentNoticeModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: DP identify/search/create
  const [searchType, setSearchType] = useState<SearchType>("DPID");
  const [dpSearchInput, setDpSearchInput] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+91"); // Default to India
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dpSearchAttempted, setDpSearchAttempted] = useState(false);
  const [dpSearchResults, setDpSearchResults] = useState<DummyDpResult[]>([]);
  const [loadingDpSearch, setLoadingDpSearch] = useState(false);
  const [dpSearchError, setDpSearchError] = useState<string | null>(null);
  const [selectedDp, setSelectedDp] = useState<DummyDpResult | null>(null);

  const [createIdentifierType, setCreateIdentifierType] = useState<IdentifierType>("EMAIL");
  const [createIdentifierValue, setCreateIdentifierValue] = useState("");
  const [createPhoneCountryCode, setCreatePhoneCountryCode] = useState("+91"); // Default to India for create
  const [createIsMinor, setCreateIsMinor] = useState(false);
  const [createGuardianType, setCreateGuardianType] = useState<IdentifierType>("PHONE");
  const [createGuardianValue, setCreateGuardianValue] = useState("");
  const [createGuardianPhoneCountryCode, setCreateGuardianPhoneCountryCode] = useState("+91"); // Default to India for guardian
  const [creatingDp, setCreatingDp] = useState(false);
  const [createDpError, setCreateDpError] = useState<string | null>(null);

  // Step 2: Notice selection (real data)
  const [selectedNoticeId, setSelectedNoticeId] = useState<number | "">("");
  const [consentNotices, setConsentNotices] = useState<CollectionPoint[]>([]);
  const [loadingNotices, setLoadingNotices] = useState(false);
  const [noticesError, setNoticesError] = useState<string | null>(null);
  const [selectedNoticeDetails, setSelectedNoticeDetails] = useState<CollectionPoint | null>(null);
  const [selectedNoticeSnapshot, setSelectedNoticeSnapshot] = useState<SnapshotStructure | null>(null);
  const [loadingNoticeDetails, setLoadingNoticeDetails] = useState(false);
  const [purposeStatuses, setPurposeStatuses] = useState<PurposeConsentStatus[]>([]);
  const [loadingPurposeStatuses, setLoadingPurposeStatuses] = useState(false);

  // Step 4: Completion state
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [assignWarningsSnackbar, setAssignWarningsSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [assignmentId, setAssignmentId] = useState<number | null>(null);
  const [otpInput, setOtpInput] = useState("");
  const [otpResendCount, setOtpResendCount] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [assignmentStatus, setAssignmentStatus] = useState<AssignmentCompletionStatus | null>(null);
  const [loadingAssignmentStatus, setLoadingAssignmentStatus] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [useCustomEffectiveDate, setUseCustomEffectiveDate] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(() => todayDateInputValue());
  const [includeOtp, setIncludeOtp] = useState(false);
  const [sentIncludeOtp, setSentIncludeOtp] = useState(false);

  // Parent/Guardian vault popup state (for minor DPs)
  const [viewParentValueDialog, setViewParentValueDialog] = useState(false);
  const [parentActualValue, setParentActualValue] = useState<string>('');
  const [parentMaskedToken, setParentMaskedToken] = useState<string>('');
  const [loadingParentValue, setLoadingParentValue] = useState(false);
  const [parentValueError, setParentValueError] = useState<string>('');
  const [copyParentSuccess, setCopyParentSuccess] = useState(false);

  // Minor's vault popup state
  const [viewMinorValueDialog, setViewMinorValueDialog] = useState(false);
  const [minorActualValue, setMinorActualValue] = useState<string>('');
  const [minorMaskedToken, setMinorMaskedToken] = useState<string>('');
  const [loadingMinorValue, setLoadingMinorValue] = useState(false);
  const [minorValueError, setMinorValueError] = useState<string>('');
  const [copyMinorSuccess, setCopyMinorSuccess] = useState(false);

  const loadConsentNotices = async () => {
    try {
      setLoadingNotices(true);
      setNoticesError(null);
      const response = await collectionPointsService.getCollectionPoints({
        filters: {
          type: "NOTICE",
          isPublished: true,
        },
        pagination: {
          page: 1,
          pageSize: 100, // Get all published notices
        },
        sortBy: "A-Z",
      });
      setConsentNotices(response.data || []);
    } catch (error) {
      console.error("Error loading consent notices:", error);
      setNoticesError(error instanceof Error ? error.message : "Failed to load consent notices");
    } finally {
      setLoadingNotices(false);
    }
  };

  const fetchPurposeStatuses = async (collectionPointId: number, dpId: number) => {
    try {
      setLoadingPurposeStatuses(true);
      const statuses = await noticesService.getPurposeStatuses(collectionPointId, dpId);
      setPurposeStatuses(statuses);
    } catch (error) {
      console.error("Error fetching purpose statuses:", error);
      // Don't show error to user - just log it, purpose statuses are optional
      setPurposeStatuses([]);
    } finally {
      setLoadingPurposeStatuses(false);
    }
  };

  const fetchNoticeDetails = async (collectionPointId: number) => {
    try {
      setLoadingNoticeDetails(true);
      const collectionPoint = await collectionPointsService.getCollectionPointById(collectionPointId);
      setSelectedNoticeDetails(collectionPoint);
      
      // Fetch snapshot if collection point is published
      if (collectionPoint.IsPublished && collectionPoint.CollectionPointIdentifier) {
        try {
          const snapshot = await collectionPointsService.getCollectionPointSnapshot(
            collectionPoint.CollectionPointIdentifier
          );
          setSelectedNoticeSnapshot(snapshot);
        } catch (snapshotError) {
          console.error('Error fetching snapshot:', snapshotError);
          setSelectedNoticeSnapshot(null);
        }
      } else {
        setSelectedNoticeSnapshot(null);
      }
    } catch (error) {
      console.error("Error fetching notice details:", error);
      setSelectedNoticeDetails(null);
      setSelectedNoticeSnapshot(null);
    } finally {
      setLoadingNoticeDetails(false);
    }
  };

  // Load consent notices (published NOTICE-type collection points) when modal opens
  useEffect(() => {
    if (open && activeStep >= 1) {
      loadConsentNotices();
    }
  }, [open, activeStep]);

  // Fetch notice details when selection changes
  useEffect(() => {
    if (selectedNoticeId && typeof selectedNoticeId === 'number') {
      fetchNoticeDetails(selectedNoticeId);
      
      // Also fetch purpose statuses if DP is selected
      if (selectedDp) {
        fetchPurposeStatuses(selectedNoticeId, selectedDp.dpId);
      }
    } else {
      setSelectedNoticeDetails(null);
      setSelectedNoticeSnapshot(null);
      setPurposeStatuses([]);
    }
  }, [selectedNoticeId, selectedDp]);

  const renderNoticePreview = () => {
    if (!selectedNoticeDetails || loadingNoticeDetails) {
      return null;
    }

    const cp = selectedNoticeDetails;
    const snapshot = selectedNoticeSnapshot;

    return (
      <Paper sx={{ p: 2, mt: 2, bgcolor: theme.palette.background.default }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Notice Preview
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Name:</strong> {cp.Name}
          </Typography>
          {cp.Description && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Description:</strong> {cp.Description}
            </Typography>
          )}
        </Box>

        {snapshot && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Notice Structure (English)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {/* Labels */}
                {snapshot.labels && snapshot.labels.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Labels:
                    </Typography>
                    {snapshot.labels.map((label: any) => {
                      const enTranslation = label.translations?.find((t: any) => t.languageCode === 'en');
                      if (enTranslation) {
                        return (
                          <Box key={label.key} sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {label.key}:
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {enTranslation.text}
                            </Typography>
                          </Box>
                        );
                      }
                      return null;
                    })}
                  </Box>
                )}

                {/* Data Elements */}
                {snapshot.dataElements && snapshot.dataElements.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Data Elements ({snapshot.dataElements.length}):
                    </Typography>
                    {snapshot.dataElements.slice(0, 10).map((de: any, idx: number) => {
                      const enName = de.translations?.find((t: any) => t.languageCode === 'en')?.name || de.name;
                      return (
                        <Chip
                          key={idx}
                          label={enName || `Element ${idx + 1}`}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      );
                    })}
                    {snapshot.dataElements.length > 10 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        +{snapshot.dataElements.length - 10} more
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Purposes */}
                {snapshot.purposes && snapshot.purposes.length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Purposes ({snapshot.purposes.length}):
                    </Typography>
                    {snapshot.purposes.slice(0, 10).map((p: any, idx: number) => {
                      const enName = p.translations?.find((t: any) => t.languageCode === 'en')?.name || p.name;
                      return (
                        <Chip
                          key={idx}
                          label={enName || `Purpose ${idx + 1}`}
                          size="small"
                          color="primary"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      );
                    })}
                    {snapshot.purposes.length > 10 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        +{snapshot.purposes.length - 10} more
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {!snapshot && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Notice structure not available. The collection point may not be published yet.
          </Typography>
        )}
      </Paper>
    );
  };

  // Convert DataPrincipalListItem to DummyDpResult format
  const convertDpListItemToResult = (item: DataPrincipalListItem): DummyDpResult => {
    // The identifierDisplay uses formatPreservingToken if available, otherwise masked identifierValue
    const identifierDisplay = item.formatPreservingToken || 
      (item.identifierValue ? `${item.identifierValue.substring(0, 1)}***@***` : `DP ${item.dpId}`) ||
      (item.externalIdentifier || `DP ${item.dpId}`);
    
    return {
      dpId: item.dpId,
      identifierType: item.identifierType as IdentifierType,
      identifierDisplay,
      formatPreservingToken: item.formatPreservingToken || null, // Store masked token separately
      vaultId: item.vaultId || null, // Store vault ID for click-to-reveal
      isMinor: item.isMinor || false,
      hasGuardianContact: false, // Will be updated when we fetch details for minor DPs
    };
  };

  // Step 3: Delivery method selection
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<DeliveryMethod | null>(null);

  const canProceedFromStep0 = Boolean(selectedDp);
  const canProceedFromStep1 = Boolean(selectedNoticeId);
  const selectedNotice = consentNotices.find((n) => n.CollectionPointId === selectedNoticeId) || null;
  const canProceedFromStep2 = Boolean(selectedNotice && selectedDp && selectedDeliveryMethod);

  // Determine the effective identifier type (guardian's for minors, DP's own for non-minors)
  const effectiveIdentifierType = useMemo(() => {
    if (!selectedDp) return null;
    // For minors, use guardian's contact type; otherwise use DP's own type
    if (selectedDp.isMinor && selectedDp.hasGuardianContact && selectedDp.parentIdentifierType) {
      return selectedDp.parentIdentifierType as IdentifierType;
    }
    return selectedDp.identifierType;
  }, [selectedDp]);

  // Auto-set delivery method when DP is selected or changes
  useEffect(() => {
    if (selectedDp && activeStep >= 2 && effectiveIdentifierType) {
      if (effectiveIdentifierType === "EMAIL") {
        setSelectedDeliveryMethod("EMAIL");
      } else if (effectiveIdentifierType === "PHONE" && !selectedDeliveryMethod) {
        // Default to SMS for PHONE if not already set
        setSelectedDeliveryMethod("SMS");
      }
    }
  }, [selectedDp, activeStep, effectiveIdentifierType, selectedDeliveryMethod]);

  // Delivery method options based on effective identifier type (guardian's for minors)
  const availableDeliveryMethods = useMemo(() => {
    if (!effectiveIdentifierType) return [];
    if (effectiveIdentifierType === "EMAIL") {
      return ["EMAIL"] as const;
    }
    // PHONE: SMS, WhatsApp, or Both
    return ["SMS", "WHATSAPP", "BOTH"] as const;
  }, [effectiveIdentifierType]);

  const deliveryChannelLabel = useMemo(() => {
    if (!selectedDp || !selectedDeliveryMethod) return null;
    const isMinor = selectedDp.isMinor;

    if (isMinor) {
      return selectedDp.hasGuardianContact 
        ? `Guardian contact (${selectedDeliveryMethod === "EMAIL" ? "Email" : selectedDeliveryMethod === "SMS" ? "SMS" : selectedDeliveryMethod === "WHATSAPP" ? "WhatsApp" : "SMS + WhatsApp"})` 
        : "Blocked (guardian required)";
    }
    
    if (selectedDeliveryMethod === "EMAIL") return "Email";
    if (selectedDeliveryMethod === "SMS") return "SMS (MSG91)";
    if (selectedDeliveryMethod === "WHATSAPP") return "WhatsApp (MSG91)";
    if (selectedDeliveryMethod === "BOTH") return "SMS + WhatsApp (MSG91)";
    return null;
  }, [selectedDp, selectedDeliveryMethod]);

  const isSendBlockedByMinorGuardian = Boolean(selectedDp?.isMinor && !selectedDp?.hasGuardianContact);
  // OTP is available if delivery method includes SMS (SMS or BOTH)
  const deliveryIncludesSms = Boolean(
    selectedDeliveryMethod === 'SMS' || selectedDeliveryMethod === 'BOTH'
  );
  const canUseOtp = Boolean(
    assignmentId &&
    sentIncludeOtp &&
    selectedDeliveryMethod &&
    deliveryIncludesSms
  );
  const needsCompletionStep = Boolean(sent);

  const resetState = () => {
    setActiveStep(0);
    setSearchType("DPID");
    setDpSearchInput("");
    setPhoneCountryCode("+91");
    setPhoneNumber("");
    setDpSearchAttempted(false);
    setSelectedDp(null);
    setCreateIdentifierType("EMAIL");
    setCreateIdentifierValue("");
    setCreatePhoneCountryCode("+91");
    setCreateIsMinor(false);
    setCreateGuardianType("PHONE");
    setCreateGuardianValue("");
    setCreateGuardianPhoneCountryCode("+91");
    setSelectedNoticeId("");
    setConsentNotices([]);
    setNoticesError(null);
    setSelectedNoticeDetails(null);
    setSelectedNoticeSnapshot(null);
    setDpSearchResults([]);
    setDpSearchError(null);
    setSelectedDeliveryMethod(null);
    setUseCustomEffectiveDate(false);
    setEffectiveDate(todayDateInputValue());
    setIncludeOtp(false);
    setSentIncludeOtp(false);
    setSent(false);
    setSending(false);
    setSendError(null);
    setAssignmentId(null);
    setOtpInput("");
    setOtpResendCount(0);
    setStatusMessage(null);
    setRefreshCount(0);
    setAssignmentStatus(null);
    setPurposeStatuses([]);
    setVerifyingOtp(false);
    setResendingOtp(false);
    setOtpError(null);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleNext = () => {
    if (activeStep === 0 && !canProceedFromStep0) return;
    if (activeStep === 1 && !canProceedFromStep1) return;
    if (activeStep === 2 && !canProceedFromStep2) return;
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  // Validation function for create DP inputs
  const validateCreateInput = (): { valid: boolean; error: string | null } => {
    // Validate main identifier
    if (createIdentifierType === "EMAIL") {
      const email = createIdentifierValue.trim();
      if (!email) {
        return { valid: false, error: "Please enter an email address" };
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return { valid: false, error: "Please enter a valid email address" };
      }
    } else if (createIdentifierType === "PHONE") {
      const cleanedPhone = createIdentifierValue.trim().replace(/\s+/g, "").replace(/\D/g, "");
      if (!cleanedPhone) {
        return { valid: false, error: "Please enter a phone number" };
      }
      if (!/^\d+$/.test(cleanedPhone)) {
        return { valid: false, error: "Phone number must contain only digits" };
      }
      // Country-specific validation
      if (createPhoneCountryCode === "+91") {
        if (cleanedPhone.length !== 10) {
          return { valid: false, error: "Indian phone number must be 10 digits" };
        }
        if (!/^[6-9]/.test(cleanedPhone)) {
          return { valid: false, error: "Indian phone number must start with 6, 7, 8, or 9" };
        }
      } else if (createPhoneCountryCode === "+1") {
        if (cleanedPhone.length !== 10) {
          return { valid: false, error: "Phone number must be 10 digits" };
        }
      } else {
        if (cleanedPhone.length < 7 || cleanedPhone.length > 15) {
          return { valid: false, error: "Phone number must be between 7 and 15 digits" };
        }
      }
    }

    // Validate guardian contact if minor
    if (createIsMinor) {
      if (!createGuardianValue.trim()) {
        return { valid: false, error: "Guardian contact is required for minors" };
      }
      if (createGuardianType === "EMAIL") {
        const email = createGuardianValue.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          return { valid: false, error: "Please enter a valid guardian email address" };
        }
      } else if (createGuardianType === "PHONE") {
        const cleanedPhone = createGuardianValue.trim().replace(/\s+/g, "").replace(/\D/g, "");
        if (!cleanedPhone) {
          return { valid: false, error: "Please enter a guardian phone number" };
        }
        if (!/^\d+$/.test(cleanedPhone)) {
          return { valid: false, error: "Guardian phone number must contain only digits" };
        }
        // Country-specific validation for guardian
        if (createGuardianPhoneCountryCode === "+91") {
          if (cleanedPhone.length !== 10) {
            return { valid: false, error: "Guardian Indian phone number must be 10 digits" };
          }
          if (!/^[6-9]/.test(cleanedPhone)) {
            return { valid: false, error: "Guardian Indian phone number must start with 6, 7, 8, or 9" };
          }
        } else if (createGuardianPhoneCountryCode === "+1") {
          if (cleanedPhone.length !== 10) {
            return { valid: false, error: "Guardian phone number must be 10 digits" };
          }
        } else {
          if (cleanedPhone.length < 7 || cleanedPhone.length > 15) {
            return { valid: false, error: "Guardian phone number must be between 7 and 15 digits" };
          }
        }
      }
    }

    return { valid: true, error: null };
  };

  // Validation functions
  const validateSearchInput = (): { valid: boolean; error: string | null } => {
    if (searchType === "PHONE") {
      const cleanedPhone = phoneNumber.trim().replace(/\s+/g, "");
      if (!cleanedPhone) {
        return { valid: false, error: "Please enter a phone number" };
      }
      // Check if it's numbers only
      if (!/^\d+$/.test(cleanedPhone)) {
        return { valid: false, error: "Phone number must contain only digits" };
      }
      // Country-specific validation
      if (phoneCountryCode === "+91") {
        // India: 10 digits, should start with 6-9
        if (cleanedPhone.length !== 10) {
          return { valid: false, error: "Indian phone number must be 10 digits" };
        }
        if (!/^[6-9]/.test(cleanedPhone)) {
          return { valid: false, error: "Indian phone number must start with 6, 7, 8, or 9" };
        }
      } else if (phoneCountryCode === "+1") {
        // US/Canada: 10 digits
        if (cleanedPhone.length !== 10) {
          return { valid: false, error: "Phone number must be 10 digits" };
        }
      } else {
        // Other countries: at least 7 digits, max 15 (ITU-T E.164)
        if (cleanedPhone.length < 7 || cleanedPhone.length > 15) {
          return { valid: false, error: "Phone number must be between 7 and 15 digits" };
        }
      }
      return { valid: true, error: null };
    } else if (searchType === "EMAIL") {
      const email = dpSearchInput.trim();
      if (!email) {
        return { valid: false, error: "Please enter an email address" };
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return { valid: false, error: "Please enter a valid email address" };
      }
      return { valid: true, error: null };
    } else if (searchType === "DPID") {
      const dpId = dpSearchInput.trim();
      if (!dpId) {
        return { valid: false, error: "Please enter a DPId" };
      }
      // DPId should be numbers only
      if (!/^\d+$/.test(dpId)) {
        return { valid: false, error: "DPId must contain only numbers" };
      }
      return { valid: true, error: null };
    } else {
      // TOKEN or EXTERNAL_ID
      const value = dpSearchInput.trim();
      if (!value) {
        return { valid: false, error: `Please enter a ${searchType.toLowerCase().replace("_", " ")}` };
      }
      return { valid: true, error: null };
    }
  };

  const handleDpSearch = async () => {
    // Validate input first
    const validation = validateSearchInput();
    if (!validation.valid) {
      setDpSearchError(validation.error);
      return;
    }

    let searchTerm = "";
    
    // Build search term based on search type
    if (searchType === "PHONE") {
      const cleanedPhone = phoneNumber.trim().replace(/\s+/g, "");
      // Combine country code and phone number
      // Remove country code from phone if user included it
      let finalPhone = cleanedPhone;
      if (finalPhone.startsWith(phoneCountryCode)) {
        finalPhone = finalPhone.substring(phoneCountryCode.length);
      } else if (finalPhone.startsWith(phoneCountryCode.replace("+", ""))) {
        finalPhone = finalPhone.substring(phoneCountryCode.replace("+", "").length);
      }
      searchTerm = `${phoneCountryCode}${finalPhone}`;
    } else {
      searchTerm = dpSearchInput.trim();
    }

    try {
      setLoadingDpSearch(true);
      setDpSearchError(null);
      setDpSearchAttempted(true);
      setSelectedDp(null);

      const result = await dataPrincipalsService.listDataPrincipals({
        search: searchTerm,
        searchType: searchType, // Pass searchType to backend for optimization
        page: 1,
        pageSize: 20, // Limit to 20 results for search
        isActive: true, // Only active DPs
      });

      const convertedResults = result.dataPrincipals.map(convertDpListItemToResult);
      setDpSearchResults(convertedResults);
    } catch (error) {
      console.error("Error searching DPs:", error);
      setDpSearchError(error instanceof Error ? error.message : "Failed to search data principals");
      setDpSearchResults([]);
    } finally {
      setLoadingDpSearch(false);
    }
  };

  const handleCreateDp = async () => {
    // Validate inputs
    const validation = validateCreateInput();
    if (!validation.valid) {
      setCreateDpError(validation.error || "Please check your input");
      return;
    }

    try {
      setCreatingDp(true);
      setCreateDpError(null);

      // Format identifier with country code if phone
      let identifier = createIdentifierValue.trim();
      if (createIdentifierType === "PHONE") {
        // Remove any existing country code and add the selected one
        const cleanedPhone = identifier.replace(/\s+/g, "").replace(/\D/g, "");
        identifier = `${createPhoneCountryCode}${cleanedPhone}`;
      }

      // Format guardian identifier with country code if phone
      let parentIdentifier: string | null = null;
      if (createIsMinor && createGuardianValue.trim()) {
        if (createGuardianType === "PHONE") {
          const cleanedPhone = createGuardianValue.trim().replace(/\s+/g, "").replace(/\D/g, "");
          parentIdentifier = `${createGuardianPhoneCountryCode}${cleanedPhone}`;
        } else {
          parentIdentifier = createGuardianValue.trim();
        }
      }

      const request = {
        identifier: identifier,
        identifierType: createIdentifierType,
        isTest: false, // Always create production DPs
        isMinor: createIsMinor,
        parentIdentifier: parentIdentifier,
        parentIdentifierType: createIsMinor && createGuardianValue.trim() ? createGuardianType : null,
      };

      const createdDp = await dataPrincipalsService.createDataPrincipal(request);

      // Convert to DummyDpResult format and auto-select
      // Note: createdDp may not include isMinor/parent fields in response, so we use request values
      const dpResult: DummyDpResult = {
        dpId: createdDp.dpId,
        identifierType: createdDp.identifierType as IdentifierType,
        identifierDisplay: createdDp.formatPreservingToken || 
          (createdDp.identifierValue ? 
            (createIdentifierType === "EMAIL" 
              ? `${createdDp.identifierValue.split("@")[0]?.[0] || "x"}***@***` 
              : `+**...****`) 
            : `DP ${createdDp.dpId}`) ||
          (createdDp.externalIdentifier || `DP ${createdDp.dpId}`),
        formatPreservingToken: createdDp.formatPreservingToken || null, // Store masked token separately
        vaultId: createdDp.vaultId || null, // Store vault ID for click-to-reveal
        isMinor: createIsMinor, // Use request value
        hasGuardianContact: createIsMinor ? Boolean(createGuardianValue.trim()) : false, // Use request value
      };

      setSelectedDp(dpResult);
      
      // Clear create form
      setCreateIdentifierValue("");
      setCreateIsMinor(false);
      setCreateGuardianValue("");
      setCreateGuardianType("PHONE");
    } catch (error: any) {
      console.error("Error creating DP:", error);
      // Check if it's an "existing DP" error - the API might return existingDP in the response
      if (error.response?.data?.existingDP) {
        const existingDp = error.response.data.existingDP;
        setCreateDpError(`DP already exists: DP ${existingDp.dpId}. Please search for it instead.`);
      } else {
        setCreateDpError(error instanceof Error ? error.message : "Failed to create data principal");
      }
    } finally {
      setCreatingDp(false);
    }
  };

  const handleSend = async () => {
    if (!selectedDp || !selectedNoticeId || !selectedDeliveryMethod) {
      setSendError("Please complete all required fields.");
      return;
    }

    try {
      setSending(true);
      setSendError(null);

      const request = {
        sourceType: 'CONSENT_NOTICE' as const,
        collectionPointId: selectedNoticeId,
        dpId: selectedDp.dpId,
        deliveryMethod: selectedDeliveryMethod,
        ...(useCustomEffectiveDate
          ? { effectiveDate: dateInputToIsoStartOfDay(effectiveDate) }
          : {}),
        ...(deliveryIncludesSms ? { includeOtp } : {}),
      };

      const { assignment } = await noticesService.assignNoticeToDPUnified(request);

      setAssignmentId(assignment.AssignmentId);
      const otpFlag =
        assignment.IncludeOtp === true ||
        assignment.IncludeOtp === 1;
      setSentIncludeOtp(otpFlag);
      setSent(true);
      
      // Build success message with delivery method(s)
      const deliveryMethodLabel = 
        selectedDeliveryMethod === 'EMAIL' ? 'Email' :
        selectedDeliveryMethod === 'SMS' ? 'SMS' :
        selectedDeliveryMethod === 'WHATSAPP' ? 'WhatsApp' :
        'SMS + WhatsApp';
      
      const otpHint = otpFlag && deliveryIncludesSms
        ? ' You can enter the OTP below or use Refresh after the DP consents in the portal.'
        : ' Use Refresh to verify consent in the portal.';
      setStatusMessage(`Consent notice sent successfully via ${deliveryMethodLabel}.${otpHint}`);
      setActiveStep(3);
    } catch (error: unknown) {
      console.error("Error sending consent notice:", error);
      setSendError(getApiErrorMessage(error, "Failed to send consent notice. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = async () => {
    if (!assignmentId) {
      setStatusMessage("No assignment ID available. Please send the notice first.");
      return;
    }

    try {
      setLoadingAssignmentStatus(true);
      setStatusMessage(null);
      const status = await noticesService.checkAssignmentStatus(assignmentId);
      setAssignmentStatus(status);
      setRefreshCount((c) => c + 1);

      if (status.status === 'COMPLETED') {
        setStatusMessage(
          `Consent completed! ${status.completedPurposes}/${status.totalRequiredPurposes} required purposes consented.`
        );
      } else {
        setStatusMessage(
          `Consent pending. ${status.completedPurposes}/${status.totalRequiredPurposes} required purposes consented.`
        );
      }
    } catch (error) {
      console.error("Error refreshing assignment status:", error);
      setStatusMessage(
        error instanceof Error ? error.message : "Failed to refresh status. Please try again."
      );
    } finally {
      setLoadingAssignmentStatus(false);
    }
  };

  const handleResendOtp = async () => {
    if (!assignmentId) {
      setOtpError("No assignment ID available. Please send the notice first.");
      return;
    }

    try {
      setResendingOtp(true);
      setOtpError(null);
      setStatusMessage(null);

      await noticesService.resendOtp(assignmentId);

      setOtpResendCount((c) => c + 1);
      setOtpInput("");
      setStatusMessage("OTP resent successfully. Previous OTP has been invalidated.");
    } catch (error: any) {
      console.error("Error resending OTP:", error);
      setOtpError(error instanceof Error ? error.message : "Failed to resend OTP. Please try again.");
    } finally {
      setResendingOtp(false);
    }
  };

  const handleSubmitOtp = async () => {
    if (!assignmentId) {
      setOtpError("No assignment ID available. Please send the notice first.");
      return;
    }

    if (!otpInput.trim()) {
      setOtpError("Please enter OTP.");
      return;
    }

    // Validate OTP format (6 digits)
    if (!/^\d{6}$/.test(otpInput.trim())) {
      setOtpError("OTP must be exactly 6 digits.");
      return;
    }

    try {
      setVerifyingOtp(true);
      setOtpError(null);
      setStatusMessage(null);

      const result = await noticesService.verifyOtp(assignmentId, otpInput.trim());

      // Clear OTP input on success
      setOtpInput("");
      
      // Update status message
      setStatusMessage(
        `OTP verified successfully! Consent created: ConsentHeaderId=${result.consentHeaderId}, DataPrincipalId=${result.dataPrincipalId}.`
      );

      // Refresh assignment status to show updated completion
      try {
        const updatedStatus = await noticesService.checkAssignmentStatus(assignmentId);
        setAssignmentStatus(updatedStatus);
        setRefreshCount((c) => c + 1);

        if (updatedStatus.status === 'COMPLETED') {
          setStatusMessage(
            `Consent completed via OTP! ${updatedStatus.completedPurposes}/${updatedStatus.totalRequiredPurposes} required purposes consented.`
          );
        }
      } catch (refreshError) {
        console.error("Error refreshing assignment status after OTP verification:", refreshError);
        // Don't fail the whole operation if refresh fails
      }
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to verify OTP. Please check the OTP and try again.";
      
      // Check for specific error types from backend
      if (errorMessage.includes("expired") || errorMessage.includes("OTP_EXPIRED")) {
        setOtpError("OTP has expired. Please request a new OTP.");
      } else if (errorMessage.includes("already used") || errorMessage.includes("OTP_ALREADY_USED")) {
        setOtpError("This OTP has already been used. Please request a new OTP.");
      } else if (errorMessage.includes("invalid") || errorMessage.includes("INVALID_OTP")) {
        setOtpError("Invalid OTP. Please check and try again.");
      } else {
        setOtpError(errorMessage);
      }
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Handle viewing parent/guardian identifier
  const handleViewParentIdentifier = async (vaultId: number) => {
    if (!vaultId) return;

    setViewParentValueDialog(true);
    setLoadingParentValue(true);
    setParentValueError('');
    setParentActualValue('');
    setParentMaskedToken('');

    try {
      // Fetch both actual value and masked token in parallel
      // Note: Vault access is automatically logged by the backend when these endpoints are called
      const [actualResponse, maskedResponse] = await Promise.allSettled([
        vaultService.getActualValue(vaultId),
        vaultService.getMaskedToken(vaultId)
      ]);

      // Handle actual value response
      if (actualResponse.status === 'fulfilled') {
        setParentActualValue(actualResponse.value.originalValue);
      } else {
        console.error('Failed to get parent actual value:', actualResponse.reason);
        if (actualResponse.reason?.response?.status === 403) {
          setParentActualValue('Insufficient permissions to view actual value');
        } else {
          setParentActualValue('Failed to retrieve actual value');
        }
      }

      // Handle masked token response
      if (maskedResponse.status === 'fulfilled') {
        setParentMaskedToken(maskedResponse.value.partiallyMaskedToken);
      } else {
        console.error('Failed to get parent masked token:', maskedResponse.reason);
        if (maskedResponse.reason?.response?.status === 403) {
          setParentMaskedToken('Insufficient permissions to view masked token');
        } else {
          setParentMaskedToken('Failed to retrieve masked token');
        }
      }
    } catch (error) {
      console.error('Error fetching parent vault values:', error);
      setParentValueError('An error occurred while retrieving the data. Please try again.');
    } finally {
      setLoadingParentValue(false);
    }
  };

  const handleCopyParentToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyParentSuccess(true);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleCloseParentDialog = () => {
    setViewParentValueDialog(false);
    setParentActualValue('');
    setParentMaskedToken('');
    setParentValueError('');
  };

  // Handle viewing minor's identifier
  const handleViewMinorIdentifier = async (vaultId: number) => {
    if (!vaultId) return;

    setViewMinorValueDialog(true);
    setLoadingMinorValue(true);
    setMinorValueError('');
    setMinorActualValue('');
    setMinorMaskedToken('');

    try {
      // Fetch both actual value and masked token in parallel
      // Note: Vault access is automatically logged by the backend when these endpoints are called
      const [actualResponse, maskedResponse] = await Promise.allSettled([
        vaultService.getActualValue(vaultId),
        vaultService.getMaskedToken(vaultId)
      ]);

      // Handle actual value response
      if (actualResponse.status === 'fulfilled') {
        setMinorActualValue(actualResponse.value.originalValue);
      } else {
        console.error('Failed to get minor actual value:', actualResponse.reason);
        if (actualResponse.reason?.response?.status === 403) {
          setMinorActualValue('Insufficient permissions to view actual value');
        } else {
          setMinorActualValue('Failed to retrieve actual value');
        }
      }

      // Handle masked token response
      if (maskedResponse.status === 'fulfilled') {
        setMinorMaskedToken(maskedResponse.value.partiallyMaskedToken);
      } else {
        console.error('Failed to get minor masked token:', maskedResponse.reason);
        if (maskedResponse.reason?.response?.status === 403) {
          setMinorMaskedToken('Insufficient permissions to view masked token');
        } else {
          setMinorMaskedToken('Failed to retrieve masked token');
        }
      }
    } catch (error) {
      console.error('Error fetching minor vault values:', error);
      setMinorValueError('An error occurred while retrieving the data. Please try again.');
    } finally {
      setLoadingMinorValue(false);
    }
  };

  const handleCopyMinorToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyMinorSuccess(true);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleCloseMinorDialog = () => {
    setViewMinorValueDialog(false);
    setMinorActualValue('');
    setMinorMaskedToken('');
    setMinorValueError('');
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="lg"
      PaperProps={{
        sx: {
          minWidth: '800px',
          maxWidth: '900px',
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle>Send Consent Notice</DialogTitle>
      <DialogContent sx={{ minHeight: '400px', pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Find an existing DP
            </Typography>

            {dpSearchError && (
              <Alert severity="error" onClose={() => setDpSearchError(null)}>
                {dpSearchError}
              </Alert>
            )}

            {searchType === "PHONE" ? (
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel id="search-type">Search By</InputLabel>
                  <Select
                    labelId="search-type"
                    label="Search By"
                    value={searchType}
                    onChange={(e) => {
                      const newSearchType = e.target.value as SearchType;
                      setSearchType(newSearchType);
                      setDpSearchInput("");
                      setPhoneNumber("");
                      setDpSearchError(null);
                      setDpSearchResults([]);
                      setDpSearchAttempted(false);
                      // Reset country code to India when switching to PHONE
                      if (newSearchType === "PHONE") {
                        setPhoneCountryCode("+91");
                      }
                    }}
                  >
                    <MenuItem value="DPID">DPId</MenuItem>
                    <MenuItem value="TOKEN">Token</MenuItem>
                    <MenuItem value="EXTERNAL_ID">External ID</MenuItem>
                    <MenuItem value="EMAIL">Email</MenuItem>
                    <MenuItem value="PHONE">Phone</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="phone-country">Country</InputLabel>
                  <Select
                    labelId="phone-country"
                    label="Country"
                    value={phoneCountryCode}
                    onChange={(e) => {
                      setPhoneCountryCode(e.target.value);
                      setDpSearchError(null); // Clear error when country changes
                    }}
                  >
                    {COUNTRY_CODES.map((cc) => (
                      <MenuItem key={cc.code} value={cc.dialCode}>
                        {cc.dialCode} ({cc.name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/\D/g, "");
                    setPhoneNumber(value);
                    setDpSearchError(null); // Clear error on input change
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !loadingDpSearch) {
                      handleDpSearch();
                    }
                  }}
                  sx={{ flexGrow: 1 }}
                  placeholder={phoneCountryCode === "+91" ? "9876543210" : "Enter phone number"}
                  inputProps={{ 
                    maxLength: phoneCountryCode === "+91" || phoneCountryCode === "+1" ? 10 : 15,
                    inputMode: "numeric"
                  }}
                  error={!!dpSearchError && searchType === "PHONE"}
                  helperText={
                    phoneCountryCode === "+91" 
                      ? "10 digits, starting with 6-9" 
                      : phoneCountryCode === "+1"
                      ? "10 digits"
                      : "7-15 digits"
                  }
                />
                <Button 
                  variant="contained" 
                  onClick={handleDpSearch} 
                  disabled={loadingDpSearch || !phoneNumber.trim()}
                  sx={{ whiteSpace: "nowrap", minWidth: 100, height: '56px' }}
                >
                  {loadingDpSearch ? <CircularProgress size={20} /> : "Find"}
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel id="search-type">Search By</InputLabel>
                  <Select
                    labelId="search-type"
                    label="Search By"
                    value={searchType}
                    onChange={(e) => {
                      const newSearchType = e.target.value as SearchType;
                      setSearchType(newSearchType);
                      setDpSearchInput("");
                      setPhoneNumber("");
                      setDpSearchError(null);
                      setDpSearchResults([]);
                      setDpSearchAttempted(false);
                      // Reset country code to India when switching to PHONE
                      if (newSearchType === "PHONE") {
                        setPhoneCountryCode("+91");
                      }
                    }}
                  >
                    <MenuItem value="DPID">DPId</MenuItem>
                    <MenuItem value="TOKEN">Token</MenuItem>
                    <MenuItem value="EXTERNAL_ID">External ID</MenuItem>
                    <MenuItem value="EMAIL">Email</MenuItem>
                    <MenuItem value="PHONE">Phone</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={
                    searchType === "DPID" ? "DPId" :
                    searchType === "TOKEN" ? "Format Preserving Token" :
                    searchType === "EXTERNAL_ID" ? "External Identifier" :
                    "Email Address"
                  }
                  value={dpSearchInput}
                  onChange={(e) => {
                    let value = e.target.value;
                    // For DPId, only allow numbers
                    if (searchType === "DPID") {
                      value = value.replace(/\D/g, "");
                    }
                    setDpSearchInput(value);
                    setDpSearchError(null); // Clear error on input change
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !loadingDpSearch) {
                      handleDpSearch();
                    }
                  }}
                  sx={{ flexGrow: 1 }}
                  type={searchType === "EMAIL" ? "email" : searchType === "DPID" ? "number" : "text"}
                  error={!!dpSearchError}
                  helperText={
                    searchType === "DPID" ? "Numbers only" :
                    searchType === "EMAIL" ? "Enter a valid email address" :
                    undefined
                  }
                />
                <Button 
                  variant="contained" 
                  onClick={handleDpSearch} 
                  disabled={loadingDpSearch || !dpSearchInput.trim()}
                  sx={{ whiteSpace: "nowrap", minWidth: 100, height: '56px' }}
                >
                  {loadingDpSearch ? <CircularProgress size={20} /> : "Find"}
                </Button>
              </Stack>
            )}

            {loadingDpSearch && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {dpSearchError && (
              <Alert severity="error">{dpSearchError}</Alert>
            )}
            {dpSearchAttempted && !loadingDpSearch && (
              <Box>
                {dpSearchResults.length === 0 ? (
                  <Alert severity="info">No matches. You can create a new DP below.</Alert>
                ) : (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {dpSearchResults.map((dp) => (
                      <Chip
                        key={dp.dpId}
                        label={`DP ${dp.dpId} · ${dp.identifierType} · ${dp.identifierDisplay}${dp.isMinor ? " (minor)" : ""}`}
                        color={selectedDp?.dpId === dp.dpId ? "primary" : "default"}
                        onClick={async () => {
                          setSelectedDp(dp);
                          // If minor, fetch details to get parent information and minor's masked token
                          if (dp.isMinor) {
                            try {
                              const dpDetails = await dataPrincipalsService.getDataPrincipal(dp.dpId);
                              setSelectedDp({
                                ...dp,
                                formatPreservingToken: dpDetails.formatPreservingToken || dp.formatPreservingToken || null, // Update minor's masked token
                                vaultId: dpDetails.vaultId || dp.vaultId || null, // Update minor's vault ID
                                hasGuardianContact: Boolean(
                                  dpDetails.parentIdentifierType &&
                                  (dpDetails.parentVaultId || dpDetails.parentIdentifierValue)
                                ),
                                parentIdentifierType: dpDetails.parentIdentifierType || null,
                                parentIdentifierValue: dpDetails.parentIdentifierValue || null,
                                parentVaultId: dpDetails.parentVaultId || null,
                                parentFormatPreservingToken: dpDetails.parentFormatPreservingToken || null,
                              });
                            } catch (error) {
                              console.error("Error fetching DP details for parent info:", error);
                              // Keep the selected DP but without parent info
                            }
                          }
                        }}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            <Divider />

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Or create a new DP
            </Typography>

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="create-idtype">Identifier Type</InputLabel>
                <Select
                  labelId="create-idtype"
                  label="Identifier Type"
                  value={createIdentifierType}
                  onChange={(e) => {
                    setCreateIdentifierType(e.target.value as IdentifierType);
                    setCreateIdentifierValue("");
                    setCreateDpError(null);
                    // Reset to India when switching to PHONE
                    if (e.target.value === "PHONE") {
                      setCreatePhoneCountryCode("+91");
                    }
                  }}
                >
                  <MenuItem value="EMAIL">EMAIL</MenuItem>
                  <MenuItem value="PHONE">PHONE</MenuItem>
                </Select>
              </FormControl>
              {createIdentifierType === "PHONE" && (
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="create-phone-country">Country</InputLabel>
                  <Select
                    labelId="create-phone-country"
                    label="Country"
                    value={createPhoneCountryCode}
                    onChange={(e) => {
                      setCreatePhoneCountryCode(e.target.value);
                      setCreateDpError(null);
                    }}
                  >
                    {COUNTRY_CODES.map((cc) => (
                      <MenuItem key={cc.code} value={cc.dialCode}>
                        {cc.dialCode} ({cc.name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                label={createIdentifierType === "EMAIL" ? "Email" : "Phone"}
                value={createIdentifierValue}
                onChange={(e) => {
                  let value = e.target.value;
                  // For phone, only allow digits
                  if (createIdentifierType === "PHONE") {
                    value = value.replace(/\D/g, "");
                  }
                  setCreateIdentifierValue(value);
                  setCreateDpError(null);
                }}
                sx={{ flexGrow: 1 }}
                type={createIdentifierType === "EMAIL" ? "email" : "tel"}
                inputProps={{
                  maxLength: createIdentifierType === "PHONE" 
                    ? (createPhoneCountryCode === "+91" || createPhoneCountryCode === "+1" ? 10 : 15)
                    : undefined,
                  inputMode: createIdentifierType === "PHONE" ? "numeric" : undefined
                }}
                placeholder={
                  createIdentifierType === "PHONE" 
                    ? (createPhoneCountryCode === "+91" ? "9876543210" : "Enter phone number")
                    : "example@email.com"
                }
                error={!!createDpError && (createDpError.includes("email") || createDpError.includes("phone"))}
                helperText={
                  createIdentifierType === "EMAIL" 
                    ? "Enter a valid email address"
                    : createIdentifierType === "PHONE"
                    ? (createPhoneCountryCode === "+91" 
                        ? "10 digits, starting with 6-9" 
                        : createPhoneCountryCode === "+1"
                        ? "10 digits"
                        : "7-15 digits")
                    : undefined
                }
              />
            </Stack>

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="create-minor">Minor?</InputLabel>
                <Select
                  labelId="create-minor"
                  label="Minor?"
                  value={createIsMinor ? "YES" : "NO"}
                  onChange={(e) => setCreateIsMinor(e.target.value === "YES")}
                >
                  <MenuItem value="NO">No</MenuItem>
                  <MenuItem value="YES">Yes</MenuItem>
                </Select>
              </FormControl>

              {createIsMinor && (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel id="create-guardian-type">Guardian Contact Type</InputLabel>
                  <Select
                    labelId="create-guardian-type"
                    label="Guardian Contact Type"
                    value={createGuardianType}
                    onChange={(e) => {
                      setCreateGuardianType(e.target.value as IdentifierType);
                      setCreateGuardianValue("");
                      setCreateDpError(null);
                      // Reset to India when switching to PHONE
                      if (e.target.value === "PHONE") {
                        setCreateGuardianPhoneCountryCode("+91");
                      }
                    }}
                  >
                    <MenuItem value="PHONE">PHONE</MenuItem>
                    <MenuItem value="EMAIL">EMAIL</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>

            {createIsMinor && (
              <Stack direction="row" spacing={2} alignItems="flex-start">
                {createGuardianType === "PHONE" && (
                  <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel id="create-guardian-phone-country">Country</InputLabel>
                    <Select
                      labelId="create-guardian-phone-country"
                      label="Country"
                      value={createGuardianPhoneCountryCode}
                      onChange={(e) => {
                        setCreateGuardianPhoneCountryCode(e.target.value);
                        setCreateDpError(null);
                      }}
                    >
                      {COUNTRY_CODES.map((cc) => (
                        <MenuItem key={cc.code} value={cc.dialCode}>
                          {cc.dialCode} ({cc.name})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label={createGuardianType === "EMAIL" ? "Guardian Email" : "Guardian Phone"}
                  value={createGuardianValue}
                  onChange={(e) => {
                    let value = e.target.value;
                    // For phone, only allow digits
                    if (createGuardianType === "PHONE") {
                      value = value.replace(/\D/g, "");
                    }
                    setCreateGuardianValue(value);
                    setCreateDpError(null);
                  }}
                  sx={{ flexGrow: 1 }}
                  type={createGuardianType === "EMAIL" ? "email" : "tel"}
                  inputProps={{
                    maxLength: createGuardianType === "PHONE" 
                      ? (createGuardianPhoneCountryCode === "+91" || createGuardianPhoneCountryCode === "+1" ? 10 : 15)
                      : undefined,
                    inputMode: createGuardianType === "PHONE" ? "numeric" : undefined
                  }}
                  placeholder={
                    createGuardianType === "PHONE" 
                      ? (createGuardianPhoneCountryCode === "+91" ? "9876543210" : "Enter phone number")
                      : "guardian@email.com"
                  }
                  error={!!createDpError && createDpError.includes("guardian")}
                  helperText={
                    createGuardianType === "EMAIL" 
                      ? "Enter a valid guardian email address"
                      : createGuardianType === "PHONE"
                      ? (createGuardianPhoneCountryCode === "+91" 
                          ? "10 digits, starting with 6-9" 
                          : createGuardianPhoneCountryCode === "+1"
                          ? "10 digits"
                          : "7-15 digits")
                      : undefined
                  }
                  fullWidth={createGuardianType === "EMAIL"}
                />
              </Stack>
            )}

            {createDpError && (
              <Alert severity="error">{createDpError}</Alert>
            )}
            <Box>
              <Button 
                variant="outlined" 
                onClick={handleCreateDp} 
                disabled={creatingDp || !createIdentifierValue.trim() || (createIsMinor && !createGuardianValue.trim())}
              >
                {creatingDp ? <CircularProgress size={20} /> : "Create and Use this DP"}
              </Button>
            </Box>

            {selectedDp && (
              <Stack spacing={1}>
                <Alert severity={selectedDp.isMinor && !selectedDp.hasGuardianContact ? "error" : "success"}>
                  {selectedDp.isMinor && selectedDp.hasGuardianContact ? (
                    <>
                      Selected: DP {selectedDp.dpId} ({selectedDp.identifierType}) · Delivery: <b>{deliveryChannelLabel}</b>
                      <br />
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                        <i>Note: For minor DPs, delivery goes to guardian's {selectedDp.parentIdentifierType} contact, not the minor's {selectedDp.identifierType}.</i>
                      </Typography>
                    </>
                  ) : (
                    <>
                      Selected: DP {selectedDp.dpId} ({selectedDp.identifierType}) · Delivery: <b>{deliveryChannelLabel}</b>
                      {selectedDp.isMinor && !selectedDp.hasGuardianContact ? " · Guardian contact required for minors." : ""}
                    </>
                  )}
                </Alert>
                {selectedDp.isMinor && selectedDp.hasGuardianContact && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      <b>Note:</b> This is a minor Data Principal. The consent notice will be sent to the guardian's contact ({selectedDp.parentIdentifierType}) instead of the minor's contact ({selectedDp.identifierType}).
                    </Typography>
                    <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                              Minor's Contact ({selectedDp.identifierType}):
                            </Typography>
                            <Chip
                              label={selectedDp.formatPreservingToken || selectedDp.identifierDisplay}
                              variant="outlined"
                              size="small"
                              onClick={() => selectedDp.vaultId && handleViewMinorIdentifier(selectedDp.vaultId)}
                              sx={{
                                cursor: selectedDp.vaultId ? 'pointer' : 'default',
                                borderColor: selectedDp.vaultId ? 'primary.main' : 'text.secondary',
                                color: selectedDp.vaultId ? 'primary.main' : 'text.secondary',
                                '&:hover': selectedDp.vaultId ? {
                                  bgcolor: 'action.hover',
                                } : {}
                              }}
                            />
                            {selectedDp.vaultId && (
                              <Typography variant="caption" color="text.secondary">
                                (Click to view actual value)
                              </Typography>
                            )}
                          </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                          Guardian Contact ({selectedDp.parentIdentifierType}):
                        </Typography>
                        <Chip
                          label={
                            selectedDp.parentFormatPreservingToken || 
                            (selectedDp.parentIdentifierValue || 
                            (selectedDp.parentVaultId ? `vault:${selectedDp.parentVaultId}` : 'Not available'))
                          }
                          variant="outlined"
                          onClick={() => selectedDp.parentVaultId && handleViewParentIdentifier(selectedDp.parentVaultId)}
                          sx={{
                            cursor: selectedDp.parentVaultId ? 'pointer' : 'default',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': selectedDp.parentVaultId ? {
                              bgcolor: 'action.hover',
                            } : {}
                          }}
                        />
                        {selectedDp.parentVaultId && (
                          <Typography variant="caption" color="text.secondary">
                            (Click to view actual value)
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Select Consent Notice
            </Typography>
            {loadingNotices ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : noticesError ? (
              <Alert severity="error">{noticesError}</Alert>
            ) : consentNotices.length === 0 ? (
              <Alert severity="warning">No published consent notices found. Please publish a NOTICE-type collection point first.</Alert>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel id="notice-select">Consent Notice</InputLabel>
                  <Select
                    labelId="notice-select"
                    label="Consent Notice"
                    value={selectedNoticeId}
                    onChange={(e) => setSelectedNoticeId(e.target.value as number)}
                  >
                    {consentNotices.map((n) => (
                      <MenuItem key={n.CollectionPointId} value={n.CollectionPointId}>
                        {n.Name} (CPID {n.CollectionPointId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {loadingNoticeDetails && (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {selectedNoticeId && renderNoticePreview()}
                
                {/* Purpose Consent Statuses */}
                {selectedDp && selectedNoticeId && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Purpose Consent Status
                    </Typography>
                    {loadingPurposeStatuses ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : purposeStatuses.length > 0 ? (
                      <Stack spacing={1}>
                        {purposeStatuses.map((purpose) => (
                          <Paper key={purpose.purposeKey} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Box sx={{ flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {purpose.purposeName}
                                  </Typography>
                                  {purpose.isRequired && (
                                    <Chip label="Required" size="small" color="primary" />
                                  )}
                                  <Chip
                                    label={purpose.consented ? "Consented" : "Not Consented"}
                                    size="small"
                                    color={purpose.consented ? "success" : "default"}
                                  />
                                  {purpose.isExpired && (
                                    <Chip label="Expired" size="small" color="error" />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  Retention: {purpose.retentionType}
                                  {purpose.retentionExpirationDate && (
                                    <> · Expires: {new Date(purpose.retentionExpirationDate).toLocaleDateString()}</>
                                  )}
                                </Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No consent records found for this DP and notice.
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}

        {activeStep === 2 && (
          <Stack spacing={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Review & Send
            </Typography>

            {!selectedDp || !selectedNotice ? (
              <Alert severity="error">Missing DP or notice selection.</Alert>
            ) : (
              <>
                {/* Review Section */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Review
                  </Typography>
                  <Stack spacing={1}>
                    <Typography>
                      <b>DP</b>: {selectedDp.dpId} · {selectedDp.identifierType} · {selectedDp.identifierDisplay}{" "}
                      {selectedDp.isMinor ? "(minor)" : ""}
                    </Typography>
                    {selectedDp.isMinor && selectedDp.hasGuardianContact && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          <b>Note:</b> This is a minor Data Principal. The consent notice will be sent to the guardian's contact ({selectedDp.parentIdentifierType}) instead of the minor's contact ({selectedDp.identifierType}).
                        </Typography>
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                              Minor's Contact ({selectedDp.identifierType}):
                            </Typography>
                            <Chip
                              label={selectedDp.formatPreservingToken || selectedDp.identifierDisplay}
                              variant="outlined"
                              size="small"
                              onClick={() => selectedDp.vaultId && handleViewMinorIdentifier(selectedDp.vaultId)}
                              sx={{
                                cursor: selectedDp.vaultId ? 'pointer' : 'default',
                                borderColor: selectedDp.vaultId ? 'primary.main' : 'text.secondary',
                                color: selectedDp.vaultId ? 'primary.main' : 'text.secondary',
                                '&:hover': selectedDp.vaultId ? {
                                  bgcolor: 'action.hover',
                                } : {}
                              }}
                            />
                            {selectedDp.vaultId && (
                              <Typography variant="caption" color="text.secondary">
                                (Click to view actual value)
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                              Guardian Contact ({selectedDp.parentIdentifierType}):
                            </Typography>
                            <Chip
                              label={
                                selectedDp.parentFormatPreservingToken || 
                                (selectedDp.parentIdentifierValue || 
                                (selectedDp.parentVaultId ? `vault:${selectedDp.parentVaultId}` : 'Not available'))
                              }
                              variant="outlined"
                              onClick={() => selectedDp.parentVaultId && handleViewParentIdentifier(selectedDp.parentVaultId)}
                              sx={{
                                cursor: selectedDp.parentVaultId ? 'pointer' : 'default',
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': selectedDp.parentVaultId ? {
                                  bgcolor: 'action.hover',
                                } : {}
                              }}
                            />
                            {selectedDp.parentVaultId && (
                              <Typography variant="caption" color="text.secondary">
                                (Click to view actual value)
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Alert>
                    )}
                    <Typography>
                      <b>Consent Notice</b>: {selectedNotice.Name} (CPID {selectedNotice.CollectionPointId})
                    </Typography>
                  </Stack>
                </Box>

                {/* Delivery Method Selection */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Select Delivery Method
                  </Typography>
                  {selectedDp.isMinor && selectedDp.hasGuardianContact && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Delivery will be sent to guardian's <b>{selectedDp.parentIdentifierType}</b> contact, not the minor's {selectedDp.identifierType}.
                      </Typography>
                    </Alert>
                  )}
                  {effectiveIdentifierType === "EMAIL" ? (
                    <Alert severity="info">
                      Delivery method: <b>Email</b> (portal link will be sent via email)
                    </Alert>
                  ) : (
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={selectedDeliveryMethod || ""}
                        onChange={(e) => setSelectedDeliveryMethod(e.target.value as DeliveryMethod)}
                      >
                        <FormControlLabel 
                          value="SMS" 
                          control={<Radio />} 
                          label={
                            includeOtp && (!useCustomEffectiveDate || effectiveDate === todayDateInputValue())
                              ? "SMS (MSG91) - OTP and portal link"
                              : "SMS (MSG91) - Portal link"
                          }
                        />
                        <FormControlLabel 
                          value="WHATSAPP" 
                          control={<Radio />} 
                          label="WhatsApp (MSG91) - Portal link only (no OTP)"
                        />
                        <FormControlLabel 
                          value="BOTH" 
                          control={<Radio />} 
                          label="Both SMS and WhatsApp"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                </Box>

                <EffectiveDateSection
                  noticeHelpVariant="CONSENT_NOTICE"
                  useCustomEffectiveDate={useCustomEffectiveDate}
                  onUseCustomEffectiveDateChange={setUseCustomEffectiveDate}
                  effectiveDate={effectiveDate}
                  onEffectiveDateChange={setEffectiveDate}
                  showIncludeOtp
                  includeOtp={includeOtp}
                  onIncludeOtpChange={setIncludeOtp}
                  deliveryIncludesSms={deliveryIncludesSms}
                />

                {/* Blocking Messages */}
                {isSendBlockedByMinorGuardian && (
                  <Alert severity="error">
                    <b>Cannot send:</b> Guardian contact is required for minors. Please ensure the DP has a guardian contact configured.
                  </Alert>
                )}
                {sendError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {sendError}
                  </Alert>
                )}

                {/* Summary */}
                {selectedDeliveryMethod && !isSendBlockedByMinorGuardian && !sendError && (
                  <Alert severity="success">
                    <b>Ready to send:</b> {deliveryChannelLabel}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}

        {activeStep === 3 && (
          <Stack spacing={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Complete
            </Typography>

            {/* Status Section */}
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Tooltip 
                  title="Use Refresh to check if consent was completed in the DP portal (works for both Email and SMS). For SMS-based flows, you can also enter OTP below."
                  arrow
                >
                  <span>
                    <Button 
                      variant="outlined" 
                      onClick={handleRefresh}
                      disabled={loadingAssignmentStatus || !assignmentId}
                      startIcon={loadingAssignmentStatus ? <CircularProgress size={16} /> : null}
                    >
                      {loadingAssignmentStatus ? "Refreshing..." : "Refresh Status"}
                    </Button>
                  </span>
                </Tooltip>
                {assignmentStatus && (
                  <Chip
                    label={assignmentStatus.status === 'COMPLETED' ? 'Completed' : 'Pending'}
                    color={assignmentStatus.status === 'COMPLETED' ? 'success' : 'default'}
                    size="small"
                  />
                )}
              </Stack>

              {/* Status Display - Single consolidated message */}
              {assignmentStatus && (
                <Alert 
                  severity={assignmentStatus.status === 'COMPLETED' ? 'success' : 'info'}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {assignmentStatus.status === 'COMPLETED' 
                      ? `Consent Completed: ${assignmentStatus.completedPurposes}/${assignmentStatus.totalRequiredPurposes} required purposes consented`
                      : `Consent Pending: ${assignmentStatus.completedPurposes}/${assignmentStatus.totalRequiredPurposes} required purposes consented`
                    }
                  </Typography>
                  {assignmentStatus.consentTimestamp && (
                    <Typography variant="caption" color="text.secondary">
                      Last Consent: {new Date(assignmentStatus.consentTimestamp).toLocaleString()}
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Purpose Details */}
              {assignmentStatus && assignmentStatus.purposeStatuses.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Purpose Details
                  </Typography>
                  <Stack spacing={1}>
                    {assignmentStatus.purposeStatuses.map((purpose) => (
                      <Paper key={purpose.purposeKey} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {purpose.purposeName}
                          </Typography>
                          <Chip
                            label={purpose.consented ? "Consented" : "Not Consented"}
                            size="small"
                            color={purpose.consented ? "success" : "default"}
                          />
                          {purpose.isExpired && (
                            <Chip label="Expired" size="small" color="error" />
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Success/Error Messages */}
            {statusMessage && (
              <Alert severity="success" onClose={() => setStatusMessage(null)}>
                {statusMessage}
              </Alert>
            )}
            {otpError && (
              <Alert severity="error" onClose={() => setOtpError(null)}>
                {otpError}
              </Alert>
            )}

            {/* OTP Entry Section - Only show if SMS delivery method was used */}
            {canUseOtp ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Enter OTP to Record Consent
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  If the DP shared the OTP with you, enter it here to record consent for all CONSENT purposes in this notice.
                </Typography>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label="6-Digit OTP"
                    value={otpInput}
                    onChange={(e) => {
                      // Only allow digits, max 6
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtpInput(value);
                      setOtpError(null); // Clear error on input change
                    }}
                    sx={{ flexGrow: 1, maxWidth: 300 }}
                    inputProps={{ 
                      inputMode: "numeric",
                      maxLength: 6
                    }}
                    error={!!otpError}
                    helperText={otpError ? undefined : "Enter the 6-digit OTP received via SMS"}
                    disabled={verifyingOtp || resendingOtp}
                    placeholder="000000"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSubmitOtp} 
                    disabled={verifyingOtp || resendingOtp || !otpInput.trim() || otpInput.trim().length !== 6}
                    sx={{ whiteSpace: "nowrap", minWidth: 120, height: '56px' }}
                    startIcon={verifyingOtp ? <CircularProgress size={16} /> : null}
                  >
                    {verifyingOtp ? "Verifying..." : "Submit OTP"}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleResendOtp} 
                    disabled={verifyingOtp || resendingOtp || !assignmentId}
                    sx={{ whiteSpace: "nowrap", minWidth: 120, height: '56px' }}
                    startIcon={resendingOtp ? <CircularProgress size={16} /> : null}
                  >
                    {resendingOtp ? "Resending..." : "Resend OTP"}
                  </Button>
                </Stack>
                {otpResendCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    OTP resend count: {otpResendCount}
                  </Typography>
                )}
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Note:</strong> OTP consent records consent for <strong>all CONSENT purposes</strong> in this notice. For selective consent, the DP/guardian must submit via the portal form.
                  </Typography>
                </Alert>
              </Box>
            ) : null}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Back
        </Button>
        {activeStep < STEPS.length - 2 ? (
          <Button 
            variant="contained" 
            onClick={handleNext} 
            disabled={
              (activeStep === 0 && !canProceedFromStep0) || 
              (activeStep === 1 && !canProceedFromStep1) ||
              (activeStep === 2 && !canProceedFromStep2)
            }
          >
            Next
          </Button>
        ) : activeStep === 2 ? (
          <Button 
            variant="contained" 
            onClick={handleSend} 
            disabled={!selectedDp || !selectedNotice || !selectedDeliveryMethod || isSendBlockedByMinorGuardian || sending}
            startIcon={sending ? <CircularProgress size={16} /> : null}
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleClose} disabled={!needsCompletionStep}>
            Done
          </Button>
        )}
      </DialogActions>

      {/* Parent/Guardian Value View Dialog */}
      <Dialog
        open={viewParentValueDialog}
        onClose={handleCloseParentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Guardian Contact Information
        </DialogTitle>
        <DialogContent>
          {loadingParentValue ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : parentValueError ? (
            <Alert severity="error">{parentValueError}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {parentMaskedToken && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Masked Token
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                      {parentMaskedToken}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyParentToClipboard(parentMaskedToken)}
                      title="Copy to clipboard"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
              {parentActualValue && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Actual Value
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                      {parentActualValue}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyParentToClipboard(parentActualValue)}
                      title="Copy to clipboard"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseParentDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Minor's Value View Dialog */}
      <Dialog
        open={viewMinorValueDialog}
        onClose={handleCloseMinorDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Minor's Contact Information
        </DialogTitle>
        <DialogContent>
          {loadingMinorValue ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : minorValueError ? (
            <Alert severity="error">{minorValueError}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {minorMaskedToken && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Masked Token
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                      {minorMaskedToken}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyMinorToClipboard(minorMaskedToken)}
                      title="Copy to clipboard"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
              {minorActualValue && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Actual Value
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                      {minorActualValue}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyMinorToClipboard(minorActualValue)}
                      title="Copy to clipboard"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMinorDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={assignWarningsSnackbar.open}
        autoHideDuration={9000}
        onClose={() => setAssignWarningsSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAssignWarningsSnackbar({ open: false, message: '' })}
          severity="warning"
          sx={{ width: '100%' }}
        >
          {assignWarningsSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Copy Success Snackbars */}
      <Snackbar
        open={copyParentSuccess}
        autoHideDuration={2000}
        onClose={() => setCopyParentSuccess(false)}
        message="Copied to clipboard"
      />
      <Snackbar
        open={copyMinorSuccess}
        autoHideDuration={2000}
        onClose={() => setCopyMinorSuccess(false)}
        message="Copied to clipboard"
      />
    </Dialog>
  );
};

export default SendConsentNoticeModal;

